import { CssBaseline } from "@mui/material";
import { Amplify } from "aws-amplify";
import ReactDOM from "react-dom/client";
import { SWRConfig } from "swr";

import { PostHogProvider } from "posthog-js/react";
import type { PostHogConfig } from "posthog-js";
import { AuthProvider } from "./providers/AuthProvider";
import { RouterProvider } from "react-router-dom";
import ThemeProvider from "./providers/ThemeProvider";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/fr";

import "./locales/i18n.js";

import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "./index.css";
import { SnackbarProvider } from "./providers/ToastProvider";
import { LimitationPlanProvider } from "./providers/LimitationPlanProvider";
import Router from "./routes";
import { Suspense } from "react";
import Loader from "./components/Loader/Loader.js";

dayjs.extend(isSameOrBefore);

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_POOL_CLIENT_ID,
    },
  },
  API: {
    REST: {
      api: {
        endpoint: import.meta.env.VITE_API_URL,
        region: import.meta.env.VITE_AWS_REGION,
      },
    },
  },
});

const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <AuthProvider>
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: true,
        // A remounted view (route change, viewport flip, a provider re-render that
        // swaps the subtree) asks for the same key again. Without this, SWR's 2s
        // default lets each remount fire a fresh request.
        dedupingInterval: 60_000,
      }}
    >
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
          <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_API_KEY} options={options}>
            <SnackbarProvider>
              <LimitationPlanProvider>
                <Suspense fallback={<Loader />}>
                  <CssBaseline enableColorScheme />
                  <RouterProvider router={Router} />
                </Suspense>
              </LimitationPlanProvider>
            </SnackbarProvider>
          </PostHogProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </SWRConfig>
  </AuthProvider>
);
